import {PayloadAction, createSlice} from '@reduxjs/toolkit';
import {DEFAULT_HIDDEN_FORECAST_VECTOR_LAYERS} from '../../utils/ForecastMap/vectorLayerVisibility';
import type {BasemapThemePreference} from '../../utils/WeatherMap/basemapTheme';
import type {ForecastVariableKey} from '../../utils/WeatherMap/forecastCatalog';

/** A layer the weather map draws in observations mode. */
export type ObservationLayer = 'stations' | 'rainRadar' | 'lightning';

/**
 * What the weather map shows, remembered on this device only -- never synced
 * to the server (FE #327, Q2). Both apps register and persist the slice
 * (mobile since its migration 36, which retired `prefs.weathermap`). The
 * selectors read it defensively all the same: app test harnesses build
 * partial stores, and a rehydrated `observations` can lack a layer.
 *
 * There is deliberately no `mode` (observations or forecast): `/map` without a
 * `mode` in the URL always opens on observations (Q1, Q4), and anything held
 * here is written to storage.
 */
export type MapPreferencesState = {
  observations: Record<ObservationLayer, boolean>;
  forecast: {
    /**
     * The last forecast map the user picked: the unified map's forecast mode
     * opens on it when the URL names none (#327 MR5c). Can point at the
     * user's own builder config, so LOGOUT clears it.
     */
    configId: number | null;
    /**
     * The wind-particle animation, on runs that carry wind: one device-wide
     * preference (not per config), kept across a sign-out. Read it through
     * `selectForecastWindParticles`, which defaults a blob stored before
     * the key existed to on.
     */
    windParticles: boolean;
    /**
     * The rail variable last chosen (#327 MR5d): what forecast mode's Auto
     * draws when the URL names none, kept across a sign-out. Null until one
     * is chosen; read it through `selectForecastVariable`, which defaults to
     * temperature.
     */
    variable: ForecastVariableKey | null;
    /**
     * The vector layers switched off (MSLP, barbs, fronts), by
     * `forecastVectorLayerKey` (type and label, not the config-layer
     * index): one device-wide preference per layer, kept across forecast
     * maps, visits and a sign-out. Starts as
     * `DEFAULT_HIDDEN_FORECAST_VECTOR_LAYERS`. Read it through
     * `selectHiddenForecastVectorLayers`, which reads a blob stored before
     * the key existed as that default.
     */
    hiddenVectorLayers: string[];
  };
  /**
   * The Map theme (#342): Auto follows the app's theme, Light and Dark
   * override it, for every map. Light until the user picks one, whatever the
   * app theme (Vincent, 2026-10-20). A device preference like the app theme,
   * kept across a sign-out. Read it through `selectBasemapThemePreference`,
   * which reads a blob stored before the key existed as Light.
   */
  basemapTheme: BasemapThemePreference;
};

const initialState: MapPreferencesState = {
  observations: {stations: true, rainRadar: true, lightning: true},
  forecast: {
    configId: null,
    windParticles: true,
    variable: null,
    hiddenVectorLayers: [...DEFAULT_HIDDEN_FORECAST_VECTOR_LAYERS],
  },
  basemapTheme: 'light',
};

const mapPreferencesSlice = createSlice({
  name: 'mapPreferences',
  initialState,
  reducers: {
    setObservationLayer: (
      state,
      {payload}: PayloadAction<{layer: ObservationLayer; enabled: boolean}>,
    ) => {
      // Spread rather than assign through: autoMergeLevel2 restores
      // `observations` exactly as stored, so it can lack a layer added later.
      state.observations = {
        ...state.observations,
        [payload.layer]: payload.enabled,
      };
    },
    /** Remember the forecast map the user picked; null forgets it. */
    setSavedForecastConfig: (
      state,
      {payload}: PayloadAction<number | null>,
    ) => {
      // Spread: a rehydrated `forecast` may predate a key (autoMergeLevel2).
      state.forecast = {...state.forecast, configId: payload};
    },
    setForecastWindParticles: (state, {payload}: PayloadAction<boolean>) => {
      state.forecast = {...state.forecast, windParticles: payload};
    },
    /** Show or hide a vector layer, by `forecastVectorLayerKey`. */
    setForecastVectorLayer: (
      state,
      {payload}: PayloadAction<{key: string; enabled: boolean}>,
    ) => {
      // Spread: a rehydrated `forecast` may predate the key, which then
      // reads as the default (as `selectHiddenForecastVectorLayers` does).
      const stored = state.forecast?.hiddenVectorLayers;
      const hidden = (
        Array.isArray(stored) ? stored : DEFAULT_HIDDEN_FORECAST_VECTOR_LAYERS
      ).filter((key) => key !== payload.key);
      state.forecast = {
        ...state.forecast,
        hiddenVectorLayers: payload.enabled ? hidden : [...hidden, payload.key],
      };
    },
    /** Remember the rail variable the user chose. */
    setForecastVariable: (
      state,
      {payload}: PayloadAction<ForecastVariableKey>,
    ) => {
      state.forecast = {...state.forecast, variable: payload};
    },
    /** Remember the Map theme the user chose. */
    setBasemapThemePreference: (
      state,
      {payload}: PayloadAction<BasemapThemePreference>,
    ) => {
      state.basemapTheme = payload;
    },
  },
  extraReducers: (builder) => {
    // The layer toggles, the wind animation, the vector layer switches, the
    // variable and the Map theme are device preferences and survive a
    // sign-out; the saved forecast map may be the user's own config and does
    // not. Handled here rather than in an app's root reducer so the rule
    // travels with the slice to both apps. Each app must keep this slice through its LOGOUT
    // reset for the case to see the old state.
    builder.addMatcher(
      (action) => action.type === 'LOGOUT',
      (state) => {
        state.forecast = {...state.forecast, configId: null};
      },
    );
  },
});

export const {
  setObservationLayer,
  setSavedForecastConfig,
  setForecastWindParticles,
  setForecastVectorLayer,
  setForecastVariable,
  setBasemapThemePreference,
} = mapPreferencesSlice.actions;

export default mapPreferencesSlice.reducer;
